import { action, computed, makeObservable, observable } from 'mobx';
import {
    get,
    getApi,
    getStaticSiteRouter,
    insertURLQuery,
    isEmpty,
    isWxWorkUA,
    post,
    redirect,
    sdkLogger,
    h5JsApi,
    STATIC_SITE_ROUTER_TYPE
} from '~/utils';
import { API, SITE_ROUTER } from '~/constants';
import { sdkLog } from './decorator';
import { fetchJsSdkConfig } from '@fx-ui/platform-sdk';
import { corpUserStore } from '../index';

/**
 * jssdk 工具类
 * 用于向业务侧暴露jssdk API
 */
export default class JsSdkModel {
    isSdkLoaded = false;
    isSdkReady = false;
    isWWOpenDataReady = false;

    constructor() {
        makeObservable(this, {
            h5JsApiReady: computed,
            isSdkReady: observable,
            setIsSdkReady: action,
            isWWOpenDataReady: observable,
            setIsWWOpenDataReady: action,
            setIsSdkLoaded: action,
            isSdkLoaded: observable
        });
        h5JsApi.ready(() => {
            this.setIsSdkReady(true);
        });
        h5JsApi.loaded(() => {
            this.setIsSdkLoaded(true);
        });
        h5JsApi.error(() => {
            /**
             * jssdk初始化失败时，不阻塞页面渲染
             * 包括：jssdk脚本加载失败、获取鉴权参数失败、sdk鉴权失败
             */
            this.setIsSdkLoaded(true);
        });
    }

    /** 当前页面所需的 H5 JSAPI 是否就绪。 */
    get h5JsApiReady() {
        return corpUserStore.hasISVEncryptedIntegration && !isWxWorkUA()
            ? this.isWWOpenDataReady
            : this.isSdkReady;
    }

    setIsSdkReady(isReady: boolean) {
        this.isSdkReady = isReady;
    }

    setIsWWOpenDataReady(isReady: boolean) {
        this.isWWOpenDataReady = isReady;
    }

    setIsSdkLoaded(isLoaded: boolean) {
        this.isSdkLoaded = isLoaded;
    }

    /**
     * 获取jssdk相关配置
     */
    getJsSdkConfig(url?: string) {
        /**
         * 如果是登录状态，则后端会在pug中注入 res.locals.tenant 参数，由此可以判断当前是否为登录状态
         * 注意，请不要滥用该参数来判断，此处为专为 jssdk 初始化服务的特殊场景
         */
        const isLogin = !isEmpty(window.jdy_tenant_info);
        /**
         * 未登录场景下（比如外链或者集成模式扫码授权登录页），取注入 pug 中的 jdy_jssdk_auth_params 参数
         * 作为接口的body参数回传给后端来获取jssdk鉴权参数
         * 和后端 @rick.yang 确认了，未登录时，如果有 tenantId，则一定会有 jsSdkScenario
         */
        const { jsSdkTenantId, jsSdkScenario } = window.jdy_jssdk_auth_params || {};
        // fetchJsSdkConfig 规定 isLogin 必须传 true/false 字面量，且 isLogin 为 false，时，tenantId 必传
        return fetchJsSdkConfig(
            isLogin
                ? { isLogin: true, url }
                : {
                      url,
                      scenario: jsSdkScenario,
                      isLogin: false,
                      tenantId: jsSdkTenantId ?? ''
                  }
        )
            .then((res) => {
                return {
                    jsSdkConfig: res.jsSdkConfig ?? {},
                    wxAgentJsSdkConfig: res.agentJsSdkConfig ?? {}
                };
            })
            .catch((err) => {
                return Promise.reject(err);
            });
    }

    @sdkLog()
    init() {
        return h5JsApi
            .init({
                fetchConfig: (url?: string) => this.getJsSdkConfig(url)
            })
            .catch((err) => {
                sdkLogger.error(err);
            });
    }

    initWWOpenData() {
        return h5JsApi
            .initWWOpenData({
                fetchConfig: (url?: string) => this.getJsSdkConfig(url)
            })
            .then(() => {
                this.setIsWWOpenDataReady(!!window.WWOpenData);
            })
            .catch((err) => {
                this.setIsWWOpenDataReady(false);
                sdkLogger.error(err);
            });
    }

    /**
     * 调用 openLink 需使用绝对路径，否则 企业微信/钉钉 内可能无法正常跳转
     * @param url
     */
    openLink(url: string) {
        return h5JsApi.openLink(url).catch(() => {
            redirect(url);
        });
    }

    /**
     * 在客户端单点登录打开链接
     *
     * @param url 目标链接
     */
    @sdkLog()
    openClientLinkWithSSO(url: any) {
        return post(getApi(API.account.get_bridge_token)).then((res) => {
            const token = get(res, 'data.token');
            const redirectURL = insertURLQuery(
                getStaticSiteRouter(
                    STATIC_SITE_ROUTER_TYPE.SITE,
                    SITE_ROUTER.AUTHENTICATION.SIGNIN_BRIDGE
                ),
                [
                    {
                        key: 'token',
                        value: token
                    },
                    {
                        key: 'redirect_uri',
                        value: encodeURIComponent(url)
                    }
                ]
            );
            // 调用 openLink 需传入绝对路径
            return this.openLink(redirectURL);
        });
    }
}
